import { computed, inject, Injectable, signal } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { Temporal } from 'temporal-polyfill'
import { filter } from 'rxjs'
import { NavGroup, RouteDef, routeDefs, routeDefsByRoot } from '../routes'
import { LocalSettingsKeys, LocalSettingsService } from '../shared/services/local-settings.service'
import { DashboardService } from '../shared/services/dashboard.service'
import { DashboardConfig } from '../shared/models/dashboardConfig'
import { sortBy } from 'lodash'

export type AppLink = Readonly<{
  title: string
  path: string
  favorite?: boolean
  timestamp?: string
}>

const maxRecentRoutes = 20

@Injectable({ providedIn: 'root' })
export class HomePageService {
  private readonly router = inject(Router)
  private readonly localSettingsService = inject(LocalSettingsService)
  private readonly dashboardService = inject(DashboardService)

  private toRecentRoute = (rawValue: unknown): AppLink | undefined => {
    if (typeof rawValue === 'object') {
      const { path, title, timestamp } = rawValue as Record<string, unknown>
      if ([path, title, timestamp].every(v => typeof v === 'string')) {
        try {
          if (path && title && Temporal.Instant.from(timestamp as string)) {
            return { path, title, timestamp } as AppLink
          }
        } catch {
          // ignore
        }
      }
    }
    return undefined
  }

  private toFavorite = (rawValue: unknown): string | undefined =>
    typeof rawValue === 'string' ? rawValue : undefined

  readonly recentRoutes = signal<readonly AppLink[]>(
    this.localSettingsService.getArrayValue(LocalSettingsKeys.recentRoutes, this.toRecentRoute),
  )

  readonly favorites = signal<ReadonlySet<string>>(new Set(
    this.localSettingsService.getArrayValue(LocalSettingsKeys.favoriteRoutes, this.toFavorite),
  ))

  private readonly routeDefs = computed((): readonly RouteDef[] =>
    routeDefs.filter(_ => _.title && !_.argPath),
  )

  constructor() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    ).subscribe((event: NavigationEnd)=>
      this.handleEvent(event),
    )
  }

  private getRecentRouteFor(path: string): AppLink | undefined {
    const createRecentRoute = (routePath: string, title: string): AppLink => ({
      path: routePath,
      title,
      timestamp: Temporal.Now.instant().toString({ fractionalSecondDigits: 3 }),
    })

    const routeDef = this.routeDefs().find(({ root, relativePath, title }) =>
      path.startsWith(`/${root}/${relativePath}`) && title != undefined,
    )

    if (routeDef) {
      return createRecentRoute(`/${routeDef.root}/${routeDef.relativePath}`, routeDef.title!)
    } else {
      const dashboard = this.dashboardService.findDashboard(path)
      if (dashboard) {
        return createRecentRoute(DashboardService.getPathname(dashboard), dashboard.display)
      }
    }
  }

  private getPath({ url }: NavigationEnd): string | undefined {
    // noinspection HttpUrlsUsage
    const urlObject = URL.parse(`http://host${url}`)
    return urlObject?.pathname
  }

  private handleEvent(navigationEnd: NavigationEnd) {
    const path = this.getPath(navigationEnd)
    if (path) {
      const recentRoute = this.getRecentRouteFor(path)
      if (recentRoute) {
        this.addRecent(recentRoute)
      }
    }
  }

  private addRecent(recent: AppLink) {
    const newValue = [
      recent,
      ...this.recentRoutes().filter(_ => _.path !== recent.path),
    ].slice(0, maxRecentRoutes)
    this.localSettingsService.putValue(LocalSettingsKeys.recentRoutes, newValue)
    this.recentRoutes.set(newValue)
  }

  private updateFavorites(newValue: readonly string[]) {
    this.favorites.set(new Set(newValue))
    this.localSettingsService.putValue(LocalSettingsKeys.favoriteRoutes, [...this.favorites()])
  }

  addFavorite(path: string) {
    this.updateFavorites([...this.favorites(), path])
  }

  removeFavorite(path: string) {
    this.updateFavorites([...this.favorites()].filter(_ => _ !== path))
  }

  getLinks(navGroup: NavGroup): readonly AppLink[] {
    const dashboards = this.dashboardService.dashboardsSignal()
      .filter(d => !d.hidden && d.nav_group === navGroup.title)

    const routeDefs = routeDefsByRoot[navGroup.root]

    const toAppLink = (title: string, path: string): AppLink => ({
      title,
      path,
      favorite: this.favorites().has(path),
    })

    const routeDefToAppLink = ({ title, relativePath }: RouteDef) =>
      toAppLink(title!, `/${navGroup.root}/${relativePath}`)

    const dashboardToAppLink = ({ display, name }: DashboardConfig) =>
      toAppLink(display, `/${navGroup.root}/${name}`)

    const getRouteDefs = (pos: 'start' | 'end') =>
      routeDefs.filter(({ menuPosition = 'start' }) => menuPosition === pos)

    return sortBy([
      ...getRouteDefs('start').map(routeDefToAppLink),
      ...dashboards.map(dashboardToAppLink),
      ...getRouteDefs('end').map(routeDefToAppLink),
    ], ({ favorite }) => favorite ? 0 : 1)
  }
}